import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell } from 'recharts';
import { CheckCircle, Clock, XCircle, Users, Home, TrendingUp, Eye, BarChart3, Loader2, Search, ChevronLeft, ChevronRight, Shield, Star, ShieldCheck, CheckSquare, Square, Trash2, Globe, CalendarDays, BookOpen, PlusCircle, Pencil, MousePointerClick } from 'lucide-react';
import GrowthHub from '../components/GrowthHub';

const API = '/api';

const STATUS_CONFIG = {
  live: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50 border-green-200', dot: 'bg-green-500' },
  pending: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  rejected: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200', dot: 'bg-red-500' },
};

const LISTING_TYPES = ['free', 'basic', 'premium', 'featured'];

function EmptyState({ msg }) {
  return <p className="text-slate-400 text-sm mt-4 text-center py-8">{msg}</p>;
}

function ApproveModal({ property, onClose, onApprove }) {
  const [listingType, setListingType] = useState('free');
  const [loading, setLoading] = useState(false);
  const handleApprove = async () => {
    setLoading(true);
    await onApprove(property.property_id, listingType);
    setLoading(false);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-3xl p-6 w-full max-w-md mx-4 shadow-2xl" data-testid="approve-modal">
        <h3 className="font-bold text-slate-900 text-lg mb-4" style={{fontFamily:'Outfit,sans-serif'}}>Approve Listing</h3>
        <p className="text-sm text-slate-600 mb-5 bg-slate-50 rounded-xl p-3">{property.title || `${property.bhk_type} ${property.property_type}`}</p>
        <div className="mb-5">
          <label className="block text-sm font-medium text-slate-700 mb-2">Listing Type</label>
          <div className="grid grid-cols-2 gap-2">
            {LISTING_TYPES.map(t => (
              <button key={t} onClick={() => setListingType(t)}
                className={`py-2.5 px-3 rounded-xl border-2 text-sm font-semibold capitalize transition-all ${listingType === t ? 'border-blue-700 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={handleApprove} disabled={loading}
            className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="confirm-approve-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {loading ? 'Approving...' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectModal({ property, onClose, onReject }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const handleReject = async () => {
    setLoading(true);
    await onReject(property.property_id, reason);
    setLoading(false);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-3xl p-6 w-full max-w-md mx-4 shadow-2xl" data-testid="reject-modal">
        <h3 className="font-bold text-slate-900 text-lg mb-4">Reject Listing</h3>
        <p className="text-sm text-slate-600 mb-4 bg-slate-50 rounded-xl p-3">{property.title}</p>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
          placeholder="Reason for rejection (optional)"
          className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-red-400 mb-4 resize-none" data-testid="rejection-reason" />
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={handleReject} disabled={loading}
            className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="confirm-reject-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {loading ? 'Rejecting...' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Chart constants ─────────────────────────────────────────────
const CHART_MARGIN = { top: 0, right: 0, left: -20, bottom: 0 };
const CHART_MARGIN_RIGHT = { top: 0, right: 16, left: 0, bottom: 0 };
const CHART_MARGIN_SM = { top: 4, right: 4, left: -20, bottom: 4 };
const CHART_MARGIN_INSET = { top: 0, right: 12, left: 8, bottom: 0 };
const CHART_MARGIN_HOURLY = { top: 4, right: 4, left: -24, bottom: 0 };
const CHART_TICK = { fontSize: 11 };
const CHART_TICK_SM = { fontSize: 10, fill: '#94a3b8' };
const CHART_TICK_DARK = { fontSize: 10, fill: '#475569' };
const CHART_TOOLTIP = { borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '12px' };
const AXIS_TICK = { fontSize: 9, fill: '#94a3b8' };
const BAR_RADIUS_TOP = [6, 6, 0, 0];
const BAR_RADIUS_SM = [4, 4, 0, 0];
const BAR_RADIUS_RIGHT = [0, 6, 6, 0];
const TYPE_INTEREST_COLORS = ['#1d4ed8', '#0891b2', '#059669', '#d97706', '#7c3aed', '#db2777', '#dc2626', '#65a30d'];
const USER_BREAKDOWN_DATA = (analytics) => [
  { name: 'Tenants', count: analytics.tenants, fill: '#1D4ED8' },
  { name: 'Owners', count: analytics.owners, fill: '#059669' },
  { name: 'Brokers', count: analytics.brokers, fill: '#D97706' },
];

export default function AdminPanelPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [approveModal, setApproveModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [bulkSelectedIds, setBulkSelectedIds] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);
  const [search, setSearch] = useState('');

  // ── Blog state (declared early to avoid TDZ in useEffect deps) ──────────
  const [blogs, setBlogs] = useState([]);
  const [blogForm, setBlogForm] = useState({
    title: '', excerpt: '', content: '', cover_image: '', category: 'general', tags: '', status: 'published'
  });
  const [blogEditId, setBlogEditId] = useState(null);
  const [blogFormOpen, setBlogFormOpen] = useState(false);
  const [blogSaving, setBlogSaving] = useState(false);

  const fetchBlogs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/blogs`, { credentials: 'same-origin' });
      const d = await res.json();
      setBlogs(d.blogs || []);
    } catch (_err) { console.error('fetchBlogs failed:', _err); }
  }, []);

  const fetchProperties = useCallback(async (status, pg = 1) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/properties?status=${status}&page=${pg}&limit=15`, { credentials: 'same-origin' });
      const data = await res.json();
      setProperties(data.properties || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(pg);
    } catch (_err) { toast.error('Failed to load properties'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/users?limit=50`, { credentials: 'same-origin' });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (_err) { toast.error('Failed to load users'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/analytics`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Analytics request failed: ${res.status}`);
      setAnalytics(await res.json());
    } catch (_err) { console.error('fetchAnalytics failed:', _err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInsights = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/visitor-insights`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Insights request failed: ${res.status}`);
      setInsights(await res.json());
    } catch (_err) { console.error('fetchInsights failed:', _err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAnalytics();
    fetchInsights();
  }, [fetchAnalytics, fetchInsights]);

  useEffect(() => {
    if (['pending', 'live', 'rejected'].includes(tab)) fetchProperties(tab, 1);
    else if (tab === 'users') fetchUsers();
    else if (tab === 'blog') fetchBlogs();
  }, [tab, fetchProperties, fetchUsers, fetchBlogs]);

  const handleApprove = async (pid, listingType) => {
    try {
      await fetch(`${API}/admin/properties/${pid}/approve`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ listing_type: listingType })
      });
      toast.success('Listing approved!');
      fetchProperties(tab, page);
      fetchAnalytics();
    } catch (_err) { toast.error('Failed to approve'); }
  };

  const handleDeleteListing = async (pid) => {
    if (!window.confirm('Permanently delete this rejected listing? This cannot be undone.')) return;
    try {
      await fetch(`${API}/properties/${pid}`, { method: 'DELETE', credentials: 'same-origin' });
      toast.success('Listing deleted permanently');
      fetchProperties(tab, page);
      fetchAnalytics();
    } catch (_err) { toast.error('Failed to delete listing'); }
  };

  const handleReject = async (pid, reason) => {
    try {
      await fetch(`${API}/admin/properties/${pid}/reject`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ reason })
      });
      toast.success('Listing rejected');
      fetchProperties(tab, page);
      fetchAnalytics();
    } catch (_err) { toast.error('Failed to reject'); }
  };

  const toggleBulkSelect = (id) => {
    setBulkSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };
  const selectAllPending = () => setBulkSelectedIds(new Set(properties.map(p => p.property_id)));
  const deselectAllPending = () => setBulkSelectedIds(new Set());

  const handleBulkApprove = async () => {
    if (bulkSelectedIds.size === 0) return;
    if (!window.confirm(`Approve ${bulkSelectedIds.size} selected listing(s) as Free plan?`)) return;
    setBulkApproving(true);
    try {
      const res = await fetch(`${API}/admin/properties/bulk-approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ property_ids: Array.from(bulkSelectedIds), listing_type: 'free' })
      });
      const data = await res.json();
      toast.success(`${data.approved} listing(s) approved and live!`);
      setBulkSelectedIds(new Set());
      fetchProperties(tab, page);
      fetchAnalytics();
    } catch (_err) { toast.error('Bulk approve failed'); }
    setBulkApproving(false);
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await fetch(`${API}/admin/users/${userId}/role`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ role })
      });
      toast.success('Role updated');
      fetchUsers();
    } catch (_err) { toast.error('Failed to update role'); }
  };

  const handleToggleVerify = async (userId) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/verify`, {
        method: 'PUT', credentials: 'same-origin'
      });
      const data = await res.json();
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, is_verified: data.is_verified } : u));
      toast.success(data.is_verified ? 'User verified!' : 'Verification removed');
    } catch (_err) { toast.error('Failed to update verification'); }
  };

  const [confirmRemoveId, setConfirmRemoveId] = useState(null);

  // Blog state moved above to avoid TDZ reference in useEffect

  const handleBlogImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error('Cover image must be under 500KB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => setBlogForm(prev => ({ ...prev, cover_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleBlogSave = async (e) => {
    e.preventDefault();
    if (!blogForm.title.trim() || !blogForm.content.trim()) {
      toast.error('Title and content are required'); return;
    }
    setBlogSaving(true);
    try {
      const payload = { ...blogForm, tags: blogForm.tags.split(',').map(t => t.trim()).filter(Boolean) };
      const url = blogEditId ? `${API}/admin/blogs/${blogEditId}` : `${API}/admin/blogs`;
      const method = blogEditId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error();
      toast.success(blogEditId ? 'Blog updated!' : 'Blog published!');
      setBlogForm({ title: '', excerpt: '', content: '', cover_image: '', category: 'general', tags: '', status: 'published' });
      setBlogEditId(null);
      setBlogFormOpen(false);
      fetchBlogs();
    } catch (_err) { toast.error('Failed to save blog'); }
    setBlogSaving(false);
  };

  const handleBlogEdit = (blog) => {
    setBlogEditId(blog.blog_id);
    setBlogForm({
      title: blog.title || '', excerpt: blog.excerpt || '',
      content: blog.content || '', cover_image: blog.cover_image || '',
      category: blog.category || 'general',
      tags: (blog.tags || []).join(', '), status: blog.status || 'published'
    });
    setBlogFormOpen(true);
    window.scrollTo(0, 0);
  };

  const handleBlogDelete = async (blogId) => {
    if (!window.confirm('Delete this blog post permanently?')) return;
    try {
      await fetch(`${API}/admin/blogs/${blogId}`, { method: 'DELETE', credentials: 'same-origin' });
      toast.success('Blog deleted');
      fetchBlogs();
    } catch (_err) { toast.error('Failed to delete blog'); }
  };

  const handleRemoveUser = async (userId) => {
    if (confirmRemoveId !== userId) { setConfirmRemoveId(userId); return; }
    try {
      const res = await fetch(`${API}/admin/users/${userId}`, { method: 'DELETE', credentials: 'same-origin' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setUsers(prev => prev.filter(u => u.user_id !== userId));
      toast.success(data.message || 'User removed');
    } catch (err) {
      toast.error(err.message || 'Failed to remove user');
    }
    setConfirmRemoveId(null);
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const TABS = [
    { id: 'pending', label: 'Pending', count: analytics?.pending_properties },
    { id: 'live', label: 'Live', count: analytics?.live_properties },
    { id: 'rejected', label: 'Rejected', count: analytics?.rejected_properties },
    { id: 'users', label: 'Users', count: analytics?.total_users },
    { id: 'analytics', label: 'Analytics' },
    { id: 'blog', label: 'Blog', count: blogs.length || undefined },
    { id: 'growth', label: 'Growth Hub', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Admin Panel | Rent Mitra</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Navbar />
      {rejectModal && <RejectModal property={rejectModal} onClose={() => setRejectModal(null)} onReject={handleReject} />}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-7">
          <div className="w-10 h-10 bg-blue-700 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>Admin Panel</h1>
            <p className="text-slate-500 text-sm">Manage listings, users, and platform settings</p>
          </div>
        </div>

        {/* Quick Analytics */}
        {analytics && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Users', value: analytics.total_users, icon: Users, color: 'bg-blue-50 text-blue-700' },
              { label: 'Live Listings', value: analytics.live_properties, icon: CheckCircle, color: 'bg-green-50 text-green-700' },
              { label: 'Pending Review', value: analytics.pending_properties, icon: Clock, color: 'bg-amber-50 text-amber-700' },
              { label: 'Featured', value: analytics.featured_properties, icon: Star, color: 'bg-amber-50 text-amber-600' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm" data-testid={`admin-stat-${s.label.toLowerCase().replace(/ /g,'-')}`}>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${s.color}`}>
                  <s.icon className="w-4 h-4" />
                </div>
                <div className="text-2xl font-bold text-slate-900">{s.value}</div>
                <div className="text-xs text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-2xl border border-slate-200 p-1.5 mb-6 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${tab === t.id ? 'bg-blue-700 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid={`admin-tab-${t.id}`}>
              {t.label}
              {t.count !== undefined && t.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tab === t.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Property Listings Table */}
        {['pending', 'live', 'rejected'].includes(tab) && (
          <div>
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
            ) : properties.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                <div className="text-3xl mb-3">📋</div>
                <p className="text-slate-500">No {tab} listings</p>
              </div>
            ) : (
              <>
                {/* Bulk approve bar — pending tab only */}
                {tab === 'pending' && (
                  <div className="bg-amber-50 border border-amber-100 rounded-2xl px-5 py-3 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-approve-bar">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={bulkSelectedIds.size === properties.length ? deselectAllPending : selectAllPending}
                        className="flex items-center gap-1.5 text-sm text-amber-700 font-semibold"
                        data-testid="select-all-pending-btn"
                      >
                        {bulkSelectedIds.size === properties.length
                          ? <CheckSquare className="w-4 h-4" />
                          : <Square className="w-4 h-4" />}
                        {bulkSelectedIds.size === properties.length ? 'Deselect All' : 'Select All'}
                      </button>
                      {bulkSelectedIds.size > 0 && (
                        <span className="text-xs text-amber-700 font-semibold bg-amber-100 px-2 py-0.5 rounded-full">
                          {bulkSelectedIds.size} selected
                        </span>
                      )}
                    </div>
                    <button
                      onClick={handleBulkApprove}
                      disabled={bulkSelectedIds.size === 0 || bulkApproving}
                      className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors"
                      data-testid="bulk-approve-btn"
                    >
                      {bulkApproving
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <CheckCircle className="w-4 h-4" />}
                      Approve Selected ({bulkSelectedIds.size})
                    </button>
                  </div>
                )}

                <div className="space-y-3" data-testid="admin-properties-list">
                  {properties.map(prop => {
                    const sc = STATUS_CONFIG[prop.status] || STATUS_CONFIG.pending;
                    const isSelected = bulkSelectedIds.has(prop.property_id);
                    return (
                      <div
                        key={prop.property_id}
                        className={`bg-white rounded-2xl border p-4 flex gap-4 items-start shadow-sm transition-all ${isSelected && tab === 'pending' ? 'border-green-400 bg-green-50/30' : 'border-slate-100'}`}
                        data-testid={`admin-prop-${prop.property_id}`}
                      >
                        {/* Checkbox for pending */}
                        {tab === 'pending' && (
                          <button
                            onClick={() => toggleBulkSelect(prop.property_id)}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${isSelected ? 'bg-green-600 border-green-600' : 'border-slate-300 hover:border-green-400'}`}
                            data-testid={`select-prop-${prop.property_id}`}
                          >
                            {isSelected && <CheckSquare className="w-3.5 h-3.5 text-white" />}
                          </button>
                        )}
                        <div className="w-20 h-16 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                          {prop.images?.[0] ? (
                            <img src={prop.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full img-placeholder flex items-center justify-center text-slate-300">🏠</div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 text-sm truncate">{prop.title || `${prop.bhk_type || ''} ${prop.property_type}`}</h4>
                          <p className="text-xs text-slate-500 mt-0.5">{prop.area}{prop.city ? `, ${prop.city}` : ''}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">₹{Number(prop.rent).toLocaleString('en-IN')}/mo</span>
                            <span className="text-xs text-slate-500">{prop.owner_name} · {prop.owner_email}</span>
                            <span className="text-xs text-slate-400">{new Date(prop.created_at).toLocaleDateString('en-IN')}</span>
                          </div>
                        </div>
                        {tab === 'pending' && (
                          <div className="flex gap-2 flex-shrink-0">
                            <button onClick={() => setApproveModal(prop)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs font-bold transition-colors" data-testid={`approve-${prop.property_id}`}>
                              <CheckCircle className="w-3.5 h-3.5" />Approve
                            </button>
                            <button onClick={() => setRejectModal(prop)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs font-bold transition-colors" data-testid={`reject-${prop.property_id}`}>
                              <XCircle className="w-3.5 h-3.5" />Reject
                            </button>
                          </div>
                        )}
                        {tab === 'live' && (
                          <div className="flex gap-2 flex-shrink-0">
                            <button onClick={() => setRejectModal(prop)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl text-xs font-semibold transition-colors" data-testid={`remove-${prop.property_id}`}>
                              Remove
                            </button>
                          </div>
                        )}
                        {tab === 'rejected' && (
                          <div className="flex gap-2 flex-shrink-0">
                            <button onClick={() => handleDeleteListing(prop.property_id)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition-colors"
                              data-testid={`delete-rejected-${prop.property_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />Delete
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-6">
                <button disabled={page === 1} onClick={() => fetchProperties(tab, page - 1)}
                  className="flex items-center gap-1 px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-50" data-testid="admin-prev-page">
                  <ChevronLeft className="w-4 h-4" />Prev
                </button>
                <span className="text-sm text-slate-600">Page {page} of {pages} ({total} total)</span>
                <button disabled={page === pages} onClick={() => fetchProperties(tab, page + 1)}
                  className="flex items-center gap-1 px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-50" data-testid="admin-next-page">
                  Next<ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div>
            <div className="mb-4">
              <div className="flex items-center gap-2 bg-white rounded-xl border border-slate-200 px-4 py-2.5 w-full max-w-sm">
                <Search className="w-4 h-4 text-slate-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search users..." className="flex-1 outline-none text-sm text-slate-900 bg-transparent" data-testid="user-search" />
              </div>
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="users-table">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Joined</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Verified</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredUsers.map(u => (
                      <tr key={u.user_id} className="hover:bg-slate-50" data-testid={`user-row-${u.user_id}`}>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            {u.picture ? (
                              <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 text-xs font-bold">
                                {u.name?.[0]?.toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-slate-900">{u.name}</div>
                              <div className="text-xs text-slate-500">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-xs text-slate-500 hidden sm:table-cell">
                          {new Date(u.created_at).toLocaleDateString('en-IN')}
                        </td>
                        <td className="px-5 py-4">
                          <select value={u.role} onChange={e => handleRoleChange(u.user_id, e.target.value)}
                            className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs bg-white outline-none capitalize" data-testid={`role-select-${u.user_id}`}>
                            <option value="tenant">Tenant</option>
                            <option value="owner">Owner</option>
                            <option value="broker">Broker</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="px-5 py-4">
                          <button
                            onClick={() => handleToggleVerify(u.user_id)}
                            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl transition-all ${u.is_verified ? 'bg-blue-100 text-blue-700 hover:bg-red-50 hover:text-red-600' : 'bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-700'}`}
                            data-testid={`verify-btn-${u.user_id}`}
                          >
                            {u.is_verified ? <ShieldCheck className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                            {u.is_verified ? 'Verified' : 'Verify'}
                          </button>
                        </td>
                        <td className="px-5 py-4">
                          {u.role !== 'admin' && (
                            confirmRemoveId === u.user_id ? (
                              <div className="flex items-center gap-1.5">
                                <button onClick={() => handleRemoveUser(u.user_id)}
                                  className="text-xs font-semibold px-3 py-1.5 rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors"
                                  data-testid={`confirm-remove-${u.user_id}`}>
                                  Confirm
                                </button>
                                <button onClick={() => setConfirmRemoveId(null)}
                                  className="text-xs font-semibold px-3 py-1.5 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                  data-testid={`cancel-remove-${u.user_id}`}>
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button onClick={() => handleRemoveUser(u.user_id)}
                                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                data-testid={`remove-user-${u.user_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />Remove
                              </button>
                            )
                          )}
                          {u.role === 'admin' && (
                            <span className="text-xs text-slate-400 px-3">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {tab === 'analytics' && (
          <div data-testid="analytics-section">
            {!analytics ? (
              <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
            ) : (
              <div className="space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {[
                    { label: 'Total Users', value: analytics.total_users, icon: Users, color: 'bg-blue-700' },
                    { label: 'Live Listings', value: analytics.live_properties, icon: CheckCircle, color: 'bg-green-600' },
                    { label: 'Pending', value: analytics.pending_properties, icon: Clock, color: 'bg-amber-500' },
                    { label: 'Total Views', value: analytics.total_views?.toLocaleString('en-IN'), icon: Eye, color: 'bg-purple-600' },
                    { label: 'Total Contacts', value: analytics.total_contacts, icon: TrendingUp, color: 'bg-rose-500' },
                  ].map(s => (
                    <div key={s.label} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex items-center gap-4">
                      <div className={`w-10 h-10 ${s.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <s.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-slate-900">{s.value ?? 0}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Visitor KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-teal-600 to-cyan-500 rounded-2xl p-5 shadow-sm text-white flex items-center gap-4" data-testid="kpi-today-visitors">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <CalendarDays className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold">{analytics.today_visitors ?? 0}</div>
                      <div className="text-teal-100 text-xs mt-0.5 font-medium">Today's Visitors</div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-indigo-600 to-blue-500 rounded-2xl p-5 shadow-sm text-white flex items-center gap-4" data-testid="kpi-weekly-visitors">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold">{analytics.weekly_visitors ?? 0}</div>
                      <div className="text-indigo-100 text-xs mt-0.5 font-medium">This Week's Visitors</div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-violet-600 to-purple-500 rounded-2xl p-5 shadow-sm text-white flex items-center gap-4" data-testid="kpi-total-visitors">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Globe className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold">{analytics.total_visitors?.toLocaleString('en-IN') ?? 0}</div>
                      <div className="text-violet-100 text-xs mt-0.5 font-medium">All-Time Visitors</div>
                    </div>
                  </div>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Monthly Growth */}
                  <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                    <h3 className="font-bold text-slate-900 text-sm mb-4">Platform Growth (Last 6 Months)</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={analytics.monthly} margin={CHART_MARGIN}>
                        <defs>
                          <linearGradient id="usersGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#1D4ED8" stopOpacity={0.18} />
                            <stop offset="95%" stopColor="#1D4ED8" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="propsGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#059669" stopOpacity={0.18} />
                            <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="month" tick={CHART_TICK} />
                        <YAxis tick={CHART_TICK} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="users" stroke="#1D4ED8" fill="url(#usersGrad)" strokeWidth={2} name="New Users" />
                        <Area type="monotone" dataKey="properties" stroke="#059669" fill="url(#propsGrad)" strokeWidth={2} name="New Listings" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Top Cities */}
                  <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                    <h3 className="font-bold text-slate-900 text-sm mb-4">Top 5 Cities by Listings</h3>
                    {analytics.top_cities?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={analytics.top_cities} layout="vertical" margin={CHART_MARGIN_RIGHT}>
                          <XAxis type="number" tick={CHART_TICK} allowDecimals={false} />
                          <YAxis dataKey="city" type="category" tick={CHART_TICK} width={80} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#1D4ED8" radius={BAR_RADIUS_RIGHT} name="Listings" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p className="text-slate-400 text-sm mt-4">No city data yet.</p>}
                  </div>
                </div>

                {/* Second charts row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* User Breakdown */}
                  <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                    <h3 className="font-bold text-slate-900 text-sm mb-4">User Role Breakdown</h3>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={USER_BREAKDOWN_DATA(analytics)} margin={CHART_MARGIN}>
                        <XAxis dataKey="name" tick={CHART_TICK} />
                        <YAxis tick={CHART_TICK} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" radius={BAR_RADIUS_TOP} name="Users">
                          {[{ fill: '#1D4ED8' }, { fill: '#059669' }, { fill: '#D97706' }].map((entry) => (
                            <rect key={entry.fill} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Category Breakdown */}
                  <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                    <h3 className="font-bold text-slate-900 text-sm mb-4">Property Category Distribution</h3>
                    {analytics.categories?.length > 0 ? (
                      <div className="space-y-2.5 mt-2">
                        {analytics.categories.slice(0, 6).map(c => {
                          const total = analytics.categories.reduce((s, x) => s + x.count, 0);
                          const pct = total ? Math.round((c.count / total) * 100) : 0;
                          return (
                            <div key={c.name}>
                              <div className="flex justify-between text-xs mb-1">
                                <span className="font-medium text-slate-700 capitalize">{c.name}</span>
                                <span className="text-slate-500">{c.count} ({pct}%)</span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : <p className="text-slate-400 text-sm mt-4">No category data yet.</p>}
                  </div>
                </div>

                {/* Daily Visitors Chart */}
                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="daily-visitors-chart">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-900 text-sm">Daily Website Visitors (Last 30 Days)</h3>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg">
                      <Globe className="w-3.5 h-3.5 text-teal-600" />
                      Unique sessions / day
                    </div>
                  </div>
                  {analytics.daily_visitors?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={analytics.daily_visitors} margin={CHART_MARGIN_SM}>
                        <defs>
                          <linearGradient id="visitorGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#0d9488" stopOpacity={1} />
                            <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.8} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="label" tick={CHART_TICK_SM} interval={4} axisLine={false} tickLine={false} />
                        <YAxis tick={CHART_TICK_SM} allowDecimals={false} axisLine={false} tickLine={false} />
                        <Tooltip
                          formatter={(v) => [v, 'Visitors']}
                          labelFormatter={(l) => `Date: ${l}`}
                          contentStyle={CHART_TOOLTIP}
                        />
                        <Bar dataKey="visitors" fill="url(#visitorGrad)" radius={BAR_RADIUS_SM} name="Visitors" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                      <Globe className="w-10 h-10 mb-2 opacity-30" />
                      <p className="text-sm">No visitor data yet — tracking has just started.</p>
                    </div>
                  )}
                </div>

                {/* Listing Plan Distribution */}
                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                  <h3 className="font-bold text-slate-900 text-sm mb-4">Listing Plan Distribution</h3>
                  <div className="flex flex-wrap gap-4">
                    {(analytics.plans || []).map(p => {
                      const colors = { featured: 'bg-amber-100 text-amber-700 border-amber-200', premium: 'bg-purple-100 text-purple-700 border-purple-200', basic: 'bg-blue-100 text-blue-700 border-blue-200', free: 'bg-slate-100 text-slate-600 border-slate-200' };
                      const cls = colors[p.name] || colors.free;
                      return (
                        <div key={p.name} className={`px-4 py-3 rounded-xl border font-semibold capitalize flex items-center gap-3 ${cls}`}>
                          <span className="text-lg font-bold">{p.count}</span>
                          <span className="text-sm">{p.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ── ENGAGEMENT INSIGHTS ── */}
                {insights && (
                  <div className="space-y-6" data-testid="engagement-insights">
                    {/* Section header */}
                    <div className="flex items-center gap-3 pt-2">
                      <div className="h-px flex-1 bg-slate-200" />
                      <span className="text-xs font-bold uppercase tracking-widest text-slate-500 whitespace-nowrap">Engagement Insights</span>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>

                    {/* Engagement KPIs */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {[
                        { label: 'Total Events Tracked', value: insights.total_events?.toLocaleString('en-IN'), icon: BarChart3, color: 'bg-slate-700' },
                        { label: 'Engaged Sessions (3+ actions)', value: insights.engaged_sessions, icon: TrendingUp, color: 'bg-emerald-600' },
                        { label: 'Engagement Rate', value: `${insights.engagement_rate}%`, icon: Eye, color: 'bg-indigo-600' },
                      ].map(k => (
                        <div key={k.label} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex items-center gap-4" data-testid={`insight-kpi-${k.label}`}>
                          <div className={`w-10 h-10 ${k.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                            <k.icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-slate-900">{k.value ?? 0}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{k.label}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Event Breakdown + Top Pages */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Event breakdown */}
                      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="event-breakdown-chart">
                        <h3 className="font-bold text-slate-900 text-sm mb-4">User Actions Breakdown</h3>
                        {insights.event_breakdown?.length > 0 ? (
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={insights.event_breakdown} layout="vertical" margin={CHART_MARGIN_INSET}>
                              <defs>
                                <linearGradient id="eventGrad" x1="0" y1="0" x2="1" y2="0">
                                  <stop offset="0%" stopColor="#4f46e5" />
                                  <stop offset="100%" stopColor="#818cf8" />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                              <XAxis type="number" tick={CHART_TICK_SM} axisLine={false} tickLine={false} />
                              <YAxis dataKey="event" type="category" tick={CHART_TICK_DARK} width={100} axisLine={false} tickLine={false} />
                              <Tooltip contentStyle={CHART_TOOLTIP} />
                              <Bar dataKey="count" fill="url(#eventGrad)" radius={BAR_RADIUS_RIGHT} name="Count" />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : <EmptyState msg="No events tracked yet." />}
                      </div>

                      {/* Top Pages */}
                      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="top-pages-chart">
                        <h3 className="font-bold text-slate-900 text-sm mb-4">Most Visited Pages</h3>
                        {insights.top_pages?.length > 0 ? (
                          <div className="space-y-2.5">
                            {insights.top_pages.slice(0, 7).map((p, i) => {
                              const max = insights.top_pages[0]?.count || 1;
                              const pct = Math.round((p.count / max) * 100);
                              const PAGE_COLORS = ['bg-blue-600','bg-indigo-500','bg-violet-500','bg-purple-500','bg-fuchsia-500','bg-pink-500','bg-rose-500'];
                              return (
                                <div key={p.page}>
                                  <div className="flex justify-between text-xs mb-1">
                                    <span className="font-medium text-slate-700 truncate max-w-[65%]">{p.page || '/'}</span>
                                    <span className="text-slate-500 font-semibold">{p.count.toLocaleString('en-IN')}</span>
                                  </div>
                                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full transition-all ${PAGE_COLORS[i % PAGE_COLORS.length]}`} style={{ width: `${pct}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : <EmptyState msg="No page data yet." />}
                      </div>
                    </div>

                    {/* Hourly Traffic */}
                    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="hourly-traffic-chart">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-slate-900 text-sm">Hourly Traffic Distribution</h3>
                        <span className="text-xs text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg">When do users visit?</span>
                      </div>
                      <ResponsiveContainer width="100%" height={180}>
                        <AreaChart data={insights.hourly_traffic} margin={CHART_MARGIN_HOURLY}>
                          <defs>
                            <linearGradient id="hourlyGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.4} />
                              <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                          <XAxis dataKey="label" tick={AXIS_TICK} interval={2} axisLine={false} tickLine={false} />
                          <YAxis tick={AXIS_TICK} allowDecimals={false} axisLine={false} tickLine={false} />
                          <Tooltip formatter={(v) => [v, 'Visits']} contentStyle={CHART_TOOLTIP} />
                          <Area type="monotone" dataKey="visits" stroke="#0ea5e9" fill="url(#hourlyGrad)" strokeWidth={2} dot={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* City Interests + Category Interests */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* City interests */}
                      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="city-interests">
                        <h3 className="font-bold text-slate-900 text-sm mb-4">City Interest (from searches &amp; views)</h3>
                        {insights.city_interests?.length > 0 ? (
                          <div className="space-y-2.5">
                            {insights.city_interests.map(c => {
                              const max = insights.city_interests[0]?.count || 1;
                              const pct = Math.round((c.count / max) * 100);
                              return (
                                <div key={c.city}>
                                  <div className="flex justify-between text-xs mb-1">
                                    <span className="font-semibold text-slate-700">{c.city}</span>
                                    <span className="text-slate-500">{c.count} events</span>
                                  </div>
                                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : <EmptyState msg="No city interest data yet." />}
                      </div>

                      {/* Property type interests */}
                      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="type-interests">
                        <h3 className="font-bold text-slate-900 text-sm mb-4">Property Type Interest</h3>
                        {insights.type_interests?.length > 0 ? (
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={insights.type_interests} margin={CHART_MARGIN_SM}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                              <XAxis dataKey="type" tick={CHART_TICK_DARK} axisLine={false} tickLine={false} />
                              <YAxis tick={CHART_TICK_SM} allowDecimals={false} axisLine={false} tickLine={false} />
                              <Tooltip contentStyle={CHART_TOOLTIP} />
                              <Bar dataKey="count" radius={BAR_RADIUS_SM} name="Events">
                                {insights.type_interests.map((entry) => (
                                  <Cell key={entry.type} fill={TYPE_INTEREST_COLORS[insights.type_interests.indexOf(entry) % TYPE_INTEREST_COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : <EmptyState msg="No property type data yet." />}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Blog Tab ──────────────────────────────────────────────────────── */}
        {tab === 'blog' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Blog Posts</h2>
                <p className="text-slate-500 text-sm">{blogs.length} post{blogs.length !== 1 ? 's' : ''} total</p>
              </div>
              <button
                onClick={() => { setBlogEditId(null); setBlogForm({ title: '', excerpt: '', content: '', cover_image: '', category: 'general', tags: '', status: 'published' }); setBlogFormOpen(true); }}
                className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm"
                data-testid="admin-new-blog-btn"
              >
                <PlusCircle className="w-4 h-4" /> New Post
              </button>
            </div>

            {/* Blog Form */}
            {blogFormOpen && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="blog-form">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-700" />
                    {blogEditId ? 'Edit Post' : 'New Blog Post'}
                  </h3>
                  <button onClick={() => setBlogFormOpen(false)} className="text-slate-400 hover:text-slate-700 text-sm">Cancel</button>
                </div>
                <form onSubmit={handleBlogSave} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Title *</label>
                      <input
                        value={blogForm.title} onChange={e => setBlogForm(p => ({ ...p, title: e.target.value }))}
                        placeholder="e.g. 5 Tips for Renting in Mumbai"
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                        data-testid="blog-title-input"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Short Excerpt (shown in cards)</label>
                      <input
                        value={blogForm.excerpt} onChange={e => setBlogForm(p => ({ ...p, excerpt: e.target.value }))}
                        placeholder="A brief summary of the post..."
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Category</label>
                      <select
                        value={blogForm.category} onChange={e => setBlogForm(p => ({ ...p, category: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 bg-white"
                      >
                        <option value="general">General</option>
                        <option value="market-trends">Market Trends</option>
                        <option value="tenant-tips">Tenant Tips</option>
                        <option value="owner-guide">Owner Guide</option>
                        <option value="legal">Legal &amp; Finance</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Status</label>
                      <select
                        value={blogForm.status} onChange={e => setBlogForm(p => ({ ...p, status: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 bg-white"
                        data-testid="blog-status-select"
                      >
                        <option value="published">Published</option>
                        <option value="draft">Draft</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Tags (comma separated)</label>
                      <input
                        value={blogForm.tags} onChange={e => setBlogForm(p => ({ ...p, tags: e.target.value }))}
                        placeholder="rental tips, mumbai, 2bhk"
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Cover Image (max 500KB)</label>
                      <input type="file" accept="image/*" onChange={handleBlogImageChange} className="w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" data-testid="blog-image-input" />
                      {blogForm.cover_image && <img src={blogForm.cover_image} alt="preview" className="mt-2 h-20 rounded-lg object-cover w-full" />}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Content * <span className="font-normal text-slate-400">(use blank lines between paragraphs)</span></label>
                      <textarea
                        value={blogForm.content} onChange={e => setBlogForm(p => ({ ...p, content: e.target.value }))}
                        rows={14} placeholder="Write your blog post here...&#10;&#10;Use a blank line between paragraphs."
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 font-mono leading-relaxed resize-y"
                        data-testid="blog-content-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button type="submit" disabled={blogSaving}
                      className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60"
                      data-testid="blog-save-btn">
                      {blogSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
                      {blogEditId ? 'Update Post' : 'Publish Post'}
                    </button>
                    <button type="button" onClick={() => setBlogFormOpen(false)} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Blog List */}
            {blogs.length === 0 && !blogFormOpen ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No blog posts yet</p>
                <p className="text-slate-400 text-sm">Click "New Post" to write your first article</p>
              </div>
            ) : (
              <div className="space-y-3">
                {blogs.map(blog => (
                  <div key={blog.blog_id} className="bg-white rounded-2xl border border-slate-100 p-4 flex gap-4 items-start shadow-sm" data-testid={`admin-blog-item-${blog.blog_id}`}>
                    {blog.cover_image && (
                      <img src={blog.cover_image} alt={blog.title} className="w-20 h-16 object-cover rounded-xl flex-shrink-0 hidden sm:block" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${blog.status === 'published' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                          {blog.status}
                        </span>
                        {blog.category && <span className="text-xs text-slate-400 capitalize">{blog.category.replace('-', ' ')}</span>}
                        <span className="text-xs text-slate-400 ml-auto flex-shrink-0">{blog.read_time} min read · {blog.views || 0} views</span>
                      </div>
                      <h4 className="font-semibold text-slate-900 text-sm line-clamp-1">{blog.title}</h4>
                      <p className="text-slate-500 text-xs line-clamp-1 mt-0.5">{blog.excerpt}</p>
                      <p className="text-slate-400 text-xs mt-1">
                        {new Date(blog.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {' · '}<a href={`/blog/${blog.slug}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">View</a>
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => handleBlogEdit(blog)} className="p-2 text-slate-400 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors" title="Edit" data-testid={`blog-edit-${blog.blog_id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleBlogDelete(blog.blog_id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" data-testid={`blog-delete-${blog.blog_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Growth Hub */}
        {tab === 'growth' && (
          <GrowthHub blogs={blogs} onRefreshBlogs={fetchBlogs} />
        )}

      </div>
    </div>
  );
}
